import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { partnerPoliciesApi } from '../../api/partner-financial-safeguarding-policies';
import { ApiError } from '../../api/client';
import { useToast } from '../../components/Toast';

const POLICY_TYPE_LABELS: Record<string, string> = {
  finance: 'Finance', procurement: 'Procurement', hr: 'HR', safeguarding: 'Safeguarding', psea: 'PSEA', anti_fraud: 'Anti-Fraud',
  anti_corruption: 'Anti-Corruption', conflict_of_interest: 'Conflict of Interest', whistleblowing: 'Whistleblowing',
  child_safeguarding: 'Child Safeguarding', data_protection: 'Data Protection', code_of_conduct: 'Code of Conduct',
  complaints_feedback: 'Complaints/Feedback', gender: 'Gender', environmental_social: 'Environmental/Social Safeguards',
};

/** Policies & Compliance matrix (spec section 15) - one row per policy type, each independently statused. */
export function PartnerPoliciesTab({ partnerId }: { partnerId: number }) {
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();
  const policiesQuery = useQuery({ queryKey: ['partner-policies', partnerId], queryFn: () => partnerPoliciesApi.list(partnerId) });

  const statusMutation = useMutation({
    mutationFn: ({ policyType, status }: { policyType: string; status: string }) => partnerPoliciesApi.setStatus(partnerId, { policyType, status }),
    onSuccess: () => { showSuccess('Status updated.'); queryClient.invalidateQueries({ queryKey: ['partner-policies', partnerId] }); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed.'),
  });

  const statusByType = new Map((policiesQuery.data?.data ?? []).map((p) => [p.policy_type, p.status]));

  return (
    <div className="panel">
      <div className="panel-body">
        <table className="data-table">
          <thead><tr><th>Policy</th><th>Status</th></tr></thead>
          <tbody>
            {Object.entries(POLICY_TYPE_LABELS).map(([type, label]) => (
              <tr key={type}>
                <td>{label}</td>
                <td>
                  <select value={statusByType.get(type) ?? 'missing'} onChange={(e) => statusMutation.mutate({ policyType: type, status: e.target.value })}>
                    <option value="missing">Missing</option>
                    <option value="partially_verified">Partially Verified</option>
                    <option value="verified">Verified</option>
                    <option value="expired">Expired</option>
                    <option value="not_applicable">Not Applicable</option>
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
